import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--color-neutral-150)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '14px'
  },
  headerBadge: {
    fontSize: '10px',
    color: 'var(--text-color-subtle)',
    backgroundColor: 'var(--color-neutral-100)',
    padding: '1px 8px',
    borderRadius: '10px',
    fontWeight: 600
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  iconBtn: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'var(--text-color-subtle)',
    fontSize: '12px',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--text-color-primary)'
    },
    '&:disabled': {
      opacity: 0.25,
      cursor: 'default',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'var(--text-color-subtle)'
      }
    }
  },
  deleteBtn: {
    '&:hover': {
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-500)'
    }
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-color-subtle)',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '8px'
  },
  fieldCount: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--text-color-subtle)',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid var(--color-neutral-100)'
  }
}))

function GroupEditor ({ group, groupIndex, totalGroups, onChange, onDelete, onMove }) {
  const classes = useStyles()
  const fieldCount = Object.keys(group.fields || {}).length

  const handleChange = useCallback((field) => (e) => {
    onChange({ ...group, [field]: e.target.value })
  }, [group, onChange])

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>{group.title || 'Group'}</span>
          <span className={classes.headerBadge}>Group</span>
        </div>
        <div className={classes.headerActions}>
          <button
            type='button'
            className={classes.iconBtn}
            disabled={groupIndex === 0}
            onClick={() => onMove(-1)}
            title='Move up'
          >
            &#9650;
          </button>
          <button
            type='button'
            className={classes.iconBtn}
            disabled={groupIndex >= totalGroups - 1}
            onClick={() => onMove(1)}
            title='Move down'
          >
            &#9660;
          </button>
          <button
            type='button'
            className={`${classes.iconBtn} ${classes.deleteBtn}`}
            onClick={onDelete}
            title='Delete group'
          >
            &#10005;
          </button>
        </div>
      </div>

      <div className={classes.sectionLabel}>Properties</div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Title'>
            <SydInput
              value={group.title || ''}
              onChange={handleChange('title')}
              placeholder='Group heading'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='ID'>
            <SydInput
              value={group.id || ''}
              onChange={handleChange('id')}
              placeholder='Unique identifier'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={12}>
          <SydLabel label='Description'>
            <SydInput
              value={group.description || ''}
              onChange={handleChange('description')}
              placeholder='Optional description shown below the group heading'
            />
          </SydLabel>
        </Grid>
      </Grid>
      <div className={classes.fieldCount}>
        {fieldCount} field{fieldCount !== 1 ? 's' : ''} in this group
      </div>
    </div>
  )
}

GroupEditor.propTypes = {
  group: PropTypes.object.isRequired,
  groupIndex: PropTypes.number.isRequired,
  totalGroups: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired
}

export default GroupEditor
